import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Coins, Search, MessageCircle, Sparkles, ArrowRight, ArrowDown,
  Check, Minus, Gift, Star, AlertTriangle, Loader2,
  Layers, TrendingUp, BadgeCheck,
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { getCanonicalUrl, getDefaultOgImage } from "@/lib/seoUtils";
import { fetchCoinPackages, type CoinPackage } from "@/services/coinPurchaseService";
import {
  fetchPublicSubscriptionPlans,
  type SubscriptionPlanResponse,
  type BaselineEntitlementsResponse,
} from "@/services/teacherSubscriptionService";
import { cn } from "@/lib/utils";
import { formatPackMinor, formatPlanMinor, packAmountMinorFor } from "@/lib/paymentAmounts";

/**
 * One public page for BOTH halves of what BookMyCoaching charges for: the teacher subscription
 * tiers and BMC Coins (what learners spend to reach a teacher). Previously the home page carried a
 * coins-only snapshot and the tiers were visible only behind a login, so no single URL explained
 * the pricing model — which is also what made it impossible to point SEO at.
 *
 * ORDER: teachers first, learners second. The tiers are the only thing on this page anyone pays a
 * recurring amount for, so they are the page's visual anchor and lead the layout; coins are a
 * top-up, explained below. The bridge section stays BELOW both halves on purpose — it explains how
 * coins behave when you teach, which cannot be read before coins themselves have been introduced.
 * If you reorder these sections, PricingPage.test.tsx asserts the order and will fail; every other
 * assertion in that file queries by role and name, so it is the only thing standing between this
 * layout and a silent swap back.
 *
 * EVERY figure on this page is fetched, never restated in the client. A public page that hardcodes
 * prices publishes stale ones silently the moment a plan or package row changes, and the existing
 * coin fallback already carried that warning. That is why there is a real error state below rather
 * than a hardcoded fallback: showing the wrong price confidently is worse than showing none. The
 * same rule covers the per-tier blurb — it is the admin-editable `description` on the plan row, not
 * marketing copy kept in step by hand.
 *
 * Currency: the backend resolves it per visitor (CloudFront country, else the X-Timezone header
 * apiFetch already sends) and the tier endpoint returns ONE currency's prices. The mockup's
 * India/International toggle is therefore not reproduced here — flipping it would have to spoof the
 * geo header, and showing tiers in one currency beside coins in another is worse than showing each
 * visitor their own. Coin packages carry both amounts, so they follow the resolved currency too.
 */

const INQUIRY_COST_COINS = 30;
const SIGNUP_GRANT_COINS = 300;

/** A dot colour per tier, so the ladder reads at a glance instead of as four near-identical cards. */
const TIER_DOT: Record<string, string> = {
  SILVER: "bg-slate-400",
  GOLD: "bg-amber-400",
  PLATINUM: "bg-violet-500",
};

const HOW_IT_WORKS = [
  { step: "1", icon: Search, title: "Browse free", desc: "Search teachers, read profiles and compare offerings. Always free, no coins needed." },
  { step: "2", icon: MessageCircle, title: `${INQUIRY_COST_COINS} coins to connect`, desc: "Found someone you like? Send an inquiry. They get your goals and can reply directly." },
  { step: "3", icon: Sparkles, title: "Top up anytime", desc: "No subscription. Buy a pack only when you run out. Purchased coins never expire." },
];

const BRIDGE = [
  { title: `Learners spend ${INQUIRY_COST_COINS} Coins`, desc: "A learner spends coins to reach a teacher." },
  { title: "Teachers spend Coins to reply", desc: "Replying to an inquiry costs the teacher coins." },
  { title: "A plan discounts that", desc: "A teacher plan lowers reply cost and tops up coins every month." },
];

/**
 * What a plan DOES, never what it is set to. Each lever here is one of the four rows BenefitList
 * renders on every card -- the cards carry the values, straight from the plan row; this carries the
 * mechanism. Writing "+150 coins" or "10% off reply cost" into this prose would put a hand-typed
 * figure on a page whose entire premise is that it has none, and it would go stale silently the
 * first time an admin edits that plan. PricingPage.test.tsx fails on any digit appearing here.
 *
 * It also stays deliberately vague about WHICH tiers include what ("where your plan covers group
 * formats", not "Gold and above"): allowedOfferingTypes, maxPublishedOfferings and the baseline are
 * all admin-editable, so any sentence naming a tier is a claim this page cannot keep true.
 */
const REACH_LEVERS = [
  {
    icon: Layers,
    title: "More ways to be found at once",
    desc: "Every offering you publish is its own entry in search, with its own subject, level and format. Your plan's published-offering limit is not a storage cap, then — it is how many separate doors lead back to you. And where your plan covers group formats, one offering can take in a whole batch of learners instead of one at a time.",
  },
  {
    icon: TrendingUp,
    title: "A lift across every search, not one slot",
    desc: "A plan carries a ranking boost that counts toward where you land in results — on your profile and on each of your offerings, for every search a learner runs, not a single promoted position or a banner. It is weighed alongside your rating and how closely you match what they asked for.",
  },
  {
    icon: Coins,
    title: "Coins already in hand when they arrive",
    desc: "Learners spend coins to reach you, and you spend coins to open the conversation back. A plan credits bonus coins to your balance with each billing period — so the inquiries that wider reach brings in are funded before they land, rather than after you notice them stacking up.",
  },
  {
    icon: MessageCircle,
    title: "Replies that cost less, so you answer all of them",
    desc: "Reach is worth only as much as you can respond to. Where your plan includes a reply discount, your first reply to each new learner costs fewer coins than the standard rate — which is what keeps a busier inbox something you work through rather than choose between.",
  },
];

const FAQS = [
  { q: "Do teachers need a paid plan?", a: "No. Teaching is free to start. A plan adds reach — more published offerings, a ranking boost, monthly bonus coins and cheaper replies." },
  { q: "Can I change my teacher plan later?", a: "Yes. You can upgrade at any time and pay only the prorated difference. Downgrades take effect at the end of your current period." },
  { q: "Is browsing really free?", a: "Yes. Searching, viewing profiles and comparing offerings never costs coins. You only spend when you contact a teacher." },
  { q: "Do my coins expire?", a: "Purchased coins never expire. Coins granted free on signup are valid for 6 months." },
];

interface BenefitRow { label: string; included: boolean }

/**
 * Benefit rows are DERIVED from the plan row, never written by hand — so a tier whose allowances
 * change in admin cannot keep advertising the old ones here.
 */
function benefitsFor(plan: SubscriptionPlanResponse): BenefitRow[] {
  const group = plan.allowedOfferingTypes.includes("GROUP");
  return [
    { label: `${plan.maxPublishedOfferings} published offering${plan.maxPublishedOfferings === 1 ? "" : "s"} · ${group ? "1:1 + Group" : "1:1 only"}`, included: true },
    { label: plan.boostScore > 0 ? `Ad Boost +${plan.boostScore}` : "No ranking boost", included: plan.boostScore > 0 },
    { label: plan.bonusCoinsPerMonth > 0 ? `+${plan.bonusCoinsPerMonth} Coins / month` : "No monthly coin bonus", included: plan.bonusCoinsPerMonth > 0 },
    { label: plan.coinDiscountPct > 0 ? `${plan.coinDiscountPct}% off reply cost` : "Standard reply cost", included: plan.coinDiscountPct > 0 },
  ];
}

function baselineBenefits(baseline: BaselineEntitlementsResponse): BenefitRow[] {
  const group = baseline.allowedOfferingTypes.includes("GROUP");
  return [
    { label: `${baseline.maxPublishedOfferings} published offering${baseline.maxPublishedOfferings === 1 ? "" : "s"} · ${group ? "1:1 + Group" : "1:1 only"}`, included: true },
    { label: "No ranking boost", included: false },
    { label: "No monthly coin bonus", included: false },
    // NOT `included: true`. benefitsFor() renders this identical label with a dash whenever the
    // tier has no coin discount, so marking it a tick here put the same fact in two different
    // columns' worth of meaning on cards sitting side by side.
    { label: "Standard reply cost", included: false },
  ];
}

function SectionHeading({ id, title, lede }: { id: string; title: string; lede: string }) {
  return (
    <div className="text-center mb-12">
      <h2 id={id} className="font-sans text-3xl sm:text-4xl font-black text-[hsl(237,52%,31%)] mb-3">{title}</h2>
      <p className="text-lg text-muted-foreground max-w-xl mx-auto">{lede}</p>
    </div>
  );
}

function BenefitList({ rows }: { rows: BenefitRow[] }) {
  return (
    <ul className="space-y-3 mb-7">
      {rows.map((b) => (
        <li key={b.label} className="flex items-start gap-2.5 text-sm">
          {b.included
            ? <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
            : <Minus className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />}
          <span className={b.included ? "text-foreground" : "text-muted-foreground"}>{b.label}</span>
        </li>
      ))}
    </ul>
  );
}

function LoadingBlock({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground" role="status">
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/**
 * Deliberately shows NO prices. On a public page a silent hardcoded fallback publishes figures that
 * may no longer be charged; an honest "can't load" is the safer failure.
 */
function PriceErrorBlock({ what }: { what: string }) {
  return (
    <div role="alert" className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3 max-w-xl mx-auto">
      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
      <div>
        <p className="text-sm font-bold text-amber-900">We couldn&apos;t load {what} right now</p>
        <p className="text-sm text-amber-800 mt-0.5">
          Rather than show you a price that might be out of date, we&apos;ve hidden them. Please refresh in a moment.
        </p>
      </div>
    </div>
  );
}

export default function PricingPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // No per-query retry override: inherit the app's QueryClient defaults like every other query, so
  // retry policy stays in one place and tests can drive the error state directly.
  const packagesQuery = useQuery({ queryKey: ["public", "coin-packages"], queryFn: fetchCoinPackages });
  const plansQuery = useQuery({ queryKey: ["public", "teacher-plans"], queryFn: fetchPublicSubscriptionPlans });

  // SCRUM-365: the plans currency exactly as returned -- no INR fallback (D3). Plan prices with
  // no currency show the bare amount; coin packs show "—" (D2).
  const rawCurrency = plansQuery.data?.currency ?? null;
  const planPrice = (minor: number) => formatPlanMinor(minor, rawCurrency);
  const packMinorOn = (pkg: CoinPackage) => packAmountMinorFor(pkg, rawCurrency);
  const packPrice = (pkg: CoinPackage) => formatPackMinor(packMinorOn(pkg), rawCurrency);
  const packages = useMemo(
    () => [...(packagesQuery.data ?? [])].sort((a, b) => a.coins - b.coins),
    [packagesQuery.data]
  );
  const plans = useMemo(
    () => [...(plansQuery.data?.plans ?? [])].sort((a, b) => a.monthlyPriceMinor - b.monthlyPriceMinor),
    [plansQuery.data]
  );
  const baseline = plansQuery.data?.baseline;

  /**
   * Reserve the blurb row only when at least one plan actually has a description. Reserving it
   * unconditionally would leave an empty gap above every price; not reserving it at all would let a
   * one-line blurb on one tier push its price out of line with the tier beside it.
   *
   * The reserved height is three lines at this size, which fits every seeded description with room
   * to spare, and is scoped to `sm:` and up -- below that the cards stack one per row, so there is
   * nothing to align to and the reservation would just be a gap. A longer description still renders
   * in full; it only drops its own price row a little. That is deliberate: these blurbs are
   * admin-editable, and silently truncating one to keep a row tidy loses real content.
   */
  const reserveDescriptionRow = plans.some((p) => Boolean(p.description));

  const title = "Pricing — Teacher Plans & BMC Coins | BookMyCoaching";
  const description =
    "How BookMyCoaching pricing works: compare teacher subscription plans, and see how learners spend BMC Coins to contact a teacher. Browsing is free; no subscription required for learners.";
  const canonicalUrl = getCanonicalUrl(pathname);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={getDefaultOgImage()} />
      </Helmet>

      <Header />

      <main>
        {/* ── Hero ── */}
        <section className="pt-20 pb-14 bg-gradient-to-b from-[hsl(237,52%,97%)] to-background">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,50%)] mb-4 px-3 py-1 rounded-full bg-[hsl(237,52%,50%)]/10">
              <Coins className="w-3 h-3" aria-hidden="true" />
              Pricing
            </span>
            <h1 className="font-sans text-4xl sm:text-5xl font-black text-[hsl(237,52%,31%)] mb-4">
              One platform. Two ways to pay.
            </h1>
            <p className="text-lg text-muted-foreground">
              Teachers start free and choose a plan when they want more reach. Learners pay with BMC
              Coins, only when they reach out.
            </p>
            <nav aria-label="Jump to a section" className="flex flex-wrap items-center justify-center gap-3 mt-7">
              <a href="#for-teachers" className="text-sm font-semibold text-[hsl(237,52%,50%)] hover:underline cursor-pointer">For teachers</a>
              <span className="text-muted-foreground" aria-hidden="true">·</span>
              <a href="#for-learners" className="text-sm font-semibold text-[hsl(237,52%,50%)] hover:underline cursor-pointer">For learners</a>
            </nav>
          </div>
        </section>

        {/* ── For teachers (subscription tiers) — the page's visual anchor, so it leads ── */}
        <section id="for-teachers" aria-labelledby="for-teachers-h" tabIndex={-1} className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <SectionHeading id="for-teachers-h" title="For teachers" lede="Teaching on BookMyCoaching is free to start. A plan is what widens how many learners see you — and keeps you able to answer the ones who write." />

            {plansQuery.isPending && <LoadingBlock label="Loading plans…" />}
            {plansQuery.isError && <PriceErrorBlock what="plan prices" />}
            {!plansQuery.isPending && !plansQuery.isError && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 items-stretch">
                  {baseline && (
                    <div role="group" aria-labelledby="tier-free-h" className="rounded-3xl border border-border bg-muted/30 p-6 flex flex-col">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/40" aria-hidden="true" />
                        <h3 id="tier-free-h" className="text-xl font-black text-foreground">Free</h3>
                      </div>
                      {reserveDescriptionRow && (
                        <p className="text-sm text-muted-foreground leading-relaxed mb-5 sm:min-h-[4.125rem]">
                          Publish, get found and reply to learners. No card, no time limit.
                        </p>
                      )}
                      <p className="text-4xl font-black text-foreground leading-none">{planPrice(0)}</p>
                      <p className="text-xs text-muted-foreground mt-2 mb-6">Free forever</p>
                      <BenefitList rows={baselineBenefits(baseline)} />
                      <Button
                        variant="outline"
                        onClick={() => navigate("/register")}
                        className="mt-auto w-full rounded-full font-bold cursor-pointer transition-colors duration-200"
                      >
                        Start free
                      </Button>
                    </div>
                  )}

                  {plans.map((plan) => {
                    const popular = plan.tier === "GOLD";
                    return (
                      <div
                        key={plan.planId}
                        role="group"
                        aria-labelledby={`tier-${plan.tier.toLowerCase()}-h`}
                        className={cn(
                          "relative rounded-3xl border bg-card p-6 flex flex-col",
                          popular
                            ? "z-10 border-[hsl(237,52%,50%)] ring-2 ring-[hsl(237,52%,50%)]/25 shadow-2xl shadow-[hsl(237,52%,31%)]/15 lg:scale-[1.04]"
                            : "border-border transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
                        )}
                      >
                        {popular && (
                          <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap inline-flex items-center gap-1 bg-[hsl(237,52%,50%)] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                            <Star className="w-3 h-3" aria-hidden="true" /> Most popular
                          </span>
                        )}
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={cn("w-2.5 h-2.5 rounded-full", TIER_DOT[plan.tier] ?? "bg-muted-foreground/40")} aria-hidden="true" />
                          <h3 id={`tier-${plan.tier.toLowerCase()}-h`} className="text-xl font-black text-foreground">{plan.name}</h3>
                        </div>
                        {reserveDescriptionRow && (
                          <p className="text-sm text-muted-foreground leading-relaxed mb-5 sm:min-h-[4.125rem]">{plan.description}</p>
                        )}
                        <p className="text-4xl font-black text-foreground leading-none">
                          {planPrice(plan.monthlyPriceMinor)}
                          <span className="text-base font-medium text-muted-foreground">/mo</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-2 mb-6">
                          or {planPrice(plan.yearlyPriceMinor)}/yr
                          {plan.yearlyDiscountPct > 0 && (
                            <span className="ml-1.5 font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-full">
                              save {plan.yearlyDiscountPct}%
                            </span>
                          )}
                        </p>
                        <BenefitList rows={benefitsFor(plan)} />
                        <Button
                          variant={popular ? "default" : "outline"}
                          onClick={() => navigate("/register")}
                          className="mt-auto w-full rounded-full font-bold cursor-pointer transition-colors duration-200"
                        >
                          Choose {plan.name}
                        </Button>
                      </div>
                    );
                  })}
                </div>

                {/* Below the cards on purpose — see REACH_LEVERS. The cards show the what; this shows
                    what it does once you are on one. */}
                <div role="group" aria-labelledby="reach-h" className="mt-16 pt-12 border-t border-border max-w-4xl mx-auto">
                  <h3 id="reach-h" className="font-sans text-2xl sm:text-3xl font-black text-[hsl(237,52%,31%)] text-center mb-3">
                    What a plan changes
                  </h3>
                  <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10">
                    A plan does not buy you a different marketplace — it moves four levers on the one
                    you are already in. Here is what each one actually does for how far you reach.
                  </p>

                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                    {REACH_LEVERS.map((lever) => (
                      <div key={lever.title}>
                        <dt className="flex items-center gap-2.5 mb-2">
                          <span className="shrink-0 w-9 h-9 rounded-xl bg-[hsl(237,52%,50%)]/10 flex items-center justify-center">
                            <lever.icon className="w-5 h-5 text-[hsl(237,52%,50%)]" aria-hidden="true" />
                          </span>
                          <span className="font-bold text-foreground">{lever.title}</span>
                        </dt>
                        <dd className="text-sm text-muted-foreground leading-relaxed">{lever.desc}</dd>
                      </div>
                    ))}
                  </dl>

                  <p className="flex items-start gap-2.5 text-sm text-muted-foreground mt-10 pt-6 border-t border-border">
                    <BadgeCheck className="w-4 h-4 text-[hsl(237,52%,50%)] shrink-0 mt-0.5" aria-hidden="true" />
                    <span>
                      Subscribed teachers also carry their tier badge — on the profile page, and on
                      every offering card a learner meets in search results.
                    </span>
                  </p>
                </div>

                <p className="text-sm text-muted-foreground text-center mt-12">
                  Upgrade at any time and pay only the prorated difference · downgrades take effect at
                  the end of your current period
                </p>
              </>
            )}
          </div>
        </section>

        {/* ── For learners (BMC Coins) ── */}
        <section id="for-learners" aria-labelledby="for-learners-h" tabIndex={-1} className="py-20 bg-slate-50 dark:bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <SectionHeading id="for-learners-h" title="For learners" lede="Browsing is free. You only spend Coins to reach a teacher." />

            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[hsl(237,52%,31%)] to-[hsl(237,52%,22%)] p-6 sm:p-7 mb-12 shadow-xl shadow-[hsl(237,52%,31%)]/20">
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/5" aria-hidden="true" />
              <div className="flex flex-col sm:flex-row items-center gap-5 justify-between relative z-10">
                <div className="flex items-start gap-3">
                  <Gift className="w-6 h-6 text-amber-300 shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <p className="text-white font-bold text-xl mb-1">{SIGNUP_GRANT_COINS} free Coins on signup</p>
                    <p className="text-white/60 text-sm leading-relaxed max-w-md">
                      Enough for <span className="text-amber-300 font-semibold">{SIGNUP_GRANT_COINS / INQUIRY_COST_COINS} teacher inquiries</span>. No card needed.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate("/register")}
                  className="shrink-0 rounded-full bg-white text-[hsl(237,52%,31%)] hover:bg-white/90 font-bold cursor-pointer transition-colors duration-200"
                >
                  Sign up free <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-14">
              {HOW_IT_WORKS.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.step} className="rounded-2xl border border-border bg-card p-5 text-center">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-3 bg-[hsl(237,52%,50%)]/10 text-[hsl(237,52%,50%)]">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <p className="text-sm font-bold text-foreground mb-1">{item.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-foreground mb-1">Coin packs</h3>
              <p className="text-sm text-muted-foreground">Larger packs cost less per coin.</p>
            </div>

            {packagesQuery.isPending && <LoadingBlock label="Loading coin packs…" />}
            {packagesQuery.isError && <PriceErrorBlock what="coin pack prices" />}
            {!packagesQuery.isPending && !packagesQuery.isError && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-4">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.code}
                      role="group"
                      aria-labelledby={`pack-${pkg.code}-h`}
                      className={cn(
                        "relative rounded-2xl border p-5 flex flex-col gap-2 transition-all duration-200",
                        pkg.popular
                          ? "bg-[hsl(237,52%,31%)] border-transparent shadow-lg shadow-[hsl(237,52%,31%)]/25"
                          : "bg-card border-border hover:shadow-md hover:-translate-y-0.5"
                      )}
                    >
                      {pkg.popular && (
                        <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 bg-amber-400 text-amber-950 text-xs font-bold px-2 py-0.5 rounded-full shadow">
                          <Star className="w-2.5 h-2.5" aria-hidden="true" /> Best value
                        </span>
                      )}
                      <h4 id={`pack-${pkg.code}-h`} className={cn("text-lg font-black leading-none", pkg.popular ? "text-white" : "text-foreground")}>
                        {pkg.coins.toLocaleString()} Coins
                      </h4>
                      <p
                        className={cn("text-xl font-bold", pkg.popular ? "text-white" : "text-foreground")}
                        aria-label={packMinorOn(pkg) === null ? "Price unavailable" : undefined}
                      >
                        {packPrice(pkg)}
                      </p>
                      {pkg.discountPct > 0 && (
                        <span className={cn(
                          "text-xs font-bold px-1.5 py-0.5 rounded-full w-fit",
                          pkg.popular ? "bg-amber-400/20 text-amber-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                        )}>
                          {pkg.discountPct}% off
                        </span>
                      )}
                      <p className={cn("text-xs border-t pt-1.5 mt-0.5", pkg.popular ? "border-white/10 text-white/50" : "border-border text-muted-foreground")}>
                        ≈ {Math.floor(pkg.coins / INQUIRY_COST_COINS)} inquiries
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Browsing and searching are always free · coins are only spent when you contact a teacher
                </p>
              </>
            )}
          </div>
        </section>

        {/* ── Bridge: how the two halves connect. Below both, because it cannot be read before
             coins themselves have been introduced. ── */}
        <section aria-labelledby="bridge-h" className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 id="bridge-h" className="text-2xl font-black text-[hsl(237,52%,31%)] text-center mb-2">How coins work when you teach</h2>
            <p className="text-muted-foreground text-center mb-10">One currency, both sides.</p>
            <ol className="grid md:grid-cols-3 gap-4 items-stretch list-none">
              {BRIDGE.map((item, i) => (
                <li key={item.title} className="relative rounded-2xl border border-border bg-card p-5">
                  <p className="text-sm font-bold text-foreground mb-1">{item.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  {i < BRIDGE.length - 1 && (
                    <ArrowDown className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground md:-rotate-90" aria-hidden="true" />
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section aria-labelledby="faq-h" className="py-20 bg-slate-50 dark:bg-muted/30">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 id="faq-h" className="text-2xl font-black text-[hsl(237,52%,31%)] text-center mb-10">Frequently asked questions</h2>
            <dl className="space-y-4">
              {FAQS.map((item) => (
                <div key={item.q} className="rounded-2xl border border-border bg-card p-5">
                  <dt className="text-sm font-bold text-foreground mb-1">{item.q}</dt>
                  <dd className="text-sm text-muted-foreground leading-relaxed">{item.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
